/**
 * The job detail "delivery progress" state (#2677). Pure: no React, no i18n.
 *
 * Import rule: the detail tab that reads this is mounted by `../jobResource`,
 * and `../hooks/useRouteMonitoringJoin` and `../panes/enRouteModel` both reach
 * back into `../jobResource`. Nothing here (or in the tab) may import those
 * three modules; type-only imports from `../board/*` are fine.
 */
import type { components } from '@/types/api.generated';

type FulfillmentJob = components['schemas']['FulfillmentJob'];
type RouteEtaReport = components['schemas']['DispatchRouteEtaReport'];

export const DELIVERY_PROGRESS_KIND = {
  loading: 'loading',
  onTime: 'onTime',
  late: 'late',
  delivered: 'delivered',
  notYet: 'notYet',
  unavailable: 'unavailable',
  closed: 'closed',
} as const;
export type DeliveryProgressKind =
  (typeof DELIVERY_PROGRESS_KIND)[keyof typeof DELIVERY_PROGRESS_KIND];

interface Estimate {
  etaMs: number;
  /** The ETA report's name for the driver. */
  driverName: string;
  workerId: string;
  /** Delivery jobs on the same route with an earlier known ETA. */
  ahead: number;
  /** The driver is delivering this job now. */
  active: boolean;
  /** Whole minutes past the window end, rounded up; only meaningful when late. */
  minutesLate: number;
}

export type DeliveryProgressState =
  | { kind: typeof DELIVERY_PROGRESS_KIND.loading }
  | ({ kind: typeof DELIVERY_PROGRESS_KIND.onTime } & Estimate)
  | ({ kind: typeof DELIVERY_PROGRESS_KIND.late } & Estimate)
  | {
      kind: typeof DELIVERY_PROGRESS_KIND.delivered;
      /** Unix ms from today's report; `null` when the report does not carry the task. */
      completedAt: number | null;
      driverName: string | null;
    }
  | { kind: typeof DELIVERY_PROGRESS_KIND.notYet; date: string }
  | { kind: typeof DELIVERY_PROGRESS_KIND.unavailable; driverName: string | null }
  | { kind: typeof DELIVERY_PROGRESS_KIND.closed };

/** The slice of a TanStack query result the state reads. */
export interface EtaRead {
  isPending: boolean;
  isError: boolean;
  data: RouteEtaReport | undefined;
}

export interface DeliveryProgressInput {
  job: FulfillmentJob;
  /** Today in the merchant timezone, `YYYY-MM-DD`. */
  today: string;
  eta: EtaRead;
  /** The delivery-flow read (it gives the window end) has not answered yet. */
  flowPending?: boolean;
  /** Today's window end (unix ms); `null` when the flow has none. */
  windowEndMs: number | null;
  /** The clock the caller renders against. The state does not read it. */
  nowMs: number;
}

/**
 * The rules run in order. "No answer yet" is `loading`, never an empty claim
 * (#2286): only an answered read that lacks the task is `unavailable`.
 */
export function deriveDeliveryProgress({
  job,
  today,
  eta,
  flowPending = false,
  windowEndMs,
}: DeliveryProgressInput): DeliveryProgressState {
  if (job.status === 'completed') {
    // Not `onfleet_updated_at`: that is the last webhook, not the delivery.
    const completed = eta.data?.drivers
      .flatMap((driver) => driver.completed)
      .find((task) => task.taskId === job.onfleet_task_id);
    return {
      kind: DELIVERY_PROGRESS_KIND.delivered,
      completedAt: completed?.completedAt ?? null,
      driverName: job.driver_name,
    };
  }
  if (job.status === 'cancelled' || job.status === 'exception') {
    return { kind: DELIVERY_PROGRESS_KIND.closed };
  }
  if (job.status === 'planned' || job.shipping_date !== today || job.onfleet_task_id === null) {
    return { kind: DELIVERY_PROGRESS_KIND.notYet, date: job.delivery_date ?? job.shipping_date };
  }
  if (eta.isPending || flowPending) return { kind: DELIVERY_PROGRESS_KIND.loading };

  const unavailable = {
    kind: DELIVERY_PROGRESS_KIND.unavailable,
    driverName: job.driver_name,
  } as const;
  if (eta.isError || !eta.data || eta.data.date !== today) return unavailable;

  for (const driver of eta.data.drivers) {
    const task = driver.tasks.find((candidate) => candidate.taskId === job.onfleet_task_id);
    if (!task) continue;
    const etaMs = task.estimatedCompletionTime;
    if (etaMs === null) return unavailable;
    // The raw report carries no stops, so each earlier task counts as one
    // delivery job here; a consolidated task is undercounted.
    const ahead = driver.tasks.filter(
      (other) => other.estimatedCompletionTime !== null && other.estimatedCompletionTime < etaMs,
    ).length;
    const late = windowEndMs !== null && etaMs > windowEndMs;
    return {
      kind: late ? DELIVERY_PROGRESS_KIND.late : DELIVERY_PROGRESS_KIND.onTime,
      etaMs,
      driverName: driver.workerName,
      workerId: driver.workerId,
      ahead,
      active: task.state === 'active',
      minutesLate: windowEndMs === null ? 0 : Math.ceil((etaMs - windowEndMs) / 60_000),
    };
  }
  return unavailable;
}
